import { useMutation, useQuery } from "@tanstack/react-query";
import { FETCH_QUERY_KEYS } from "src/queries/consts";
import { getLanguages } from "src/components/collections/helpers";
import assistantsService, { fetchDraft } from "src/queries/services/assistants";
import {
  assistantIdSelector,
  hasPermissionSelector,
  selectedAccountIdSelector,
  selectedAssistantSelector,
  systemModeSelector,
  previousSystemModeSelector
} from "src/stores/selectors/appSelectors";
import { policiesDataSelector } from "src/stores/selectors/policiesSelectors";
import { useEffect } from "react";
import { SYSTEM_MODES } from "src/stores/slices/createAppSlice";
import useAppStore from "src/stores/appStore";
import { BUILDING_BLOCKS, INTEGRATIONS, SKILLS, CACHE_DURATION, CONFIGURATION_VERSIONS } from "@consts";
import { filterCollectionsByEnv } from "@utils";
import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import { getPolicyCrossReferenceData } from "src/queries/services/policies";
import { downloadJsonFile } from "src/utils/core/utils";

export const useFetchAssistant = (params) => {
  const selectedAssistant = useAppStore(selectedAssistantSelector);
  const { data: assistant, isError, error, isLoading } = useQuery({
    queryKey: [FETCH_QUERY_KEYS.ASSISTANT, params.accountId, params.assistantId],
    queryFn: async () => await assistantsService.fetchAssistant(params),
    enabled: !!params.accountId && !!params.assistantId,
    staleTime: Infinity
  });

  useEffect(() => {
    if (selectedAssistant?.assistantId !== assistant?.assistantId) {
      useAppStore.getState().setSelectedAssistant(assistant);
    }
  }, [assistant]);

  useEffect(() => {
    if (error) {
      useAppStore.getState().setPageError(`Failed in fetching assistant: ${params.assistantId}`, error);
    }
  }, [params.assistantId, error]);

  return { assistant, isError, isLoading };
};

export const useDraft = ({ accountId, assistantId } = {}) => {
  const selectedAssistant = useAppStore(selectedAssistantSelector);
  const stateAccountId = useAppStore(selectedAccountIdSelector);
  const usedAccount = accountId || stateAccountId;
  const usedAssistant = assistantId || selectedAssistant?.assistantId;

  const { data, isLoading, isError } = useQuery({
    queryKey: [FETCH_QUERY_KEYS.DRAFT, usedAccount, usedAssistant],
    queryFn: async () => await fetchDraft(usedAccount, usedAssistant),
    enabled: !!usedAccount && !!usedAssistant,
    staleTime: CACHE_DURATION.ONE_MINUTE
  });

  useEffect(() => {
    useAppStore.getState().setDraft(data);
  }, [data]);

  return { data, isLoading, isError };
};

export const useFetchTemplates = (params, isAccountPage) => {
  const { data, isError, isLoading, refetch } = useQuery({
    queryKey: [FETCH_QUERY_KEYS.ASSISTANT_TEMPLATES, params.channel, params.telephonyType, params.engagementType, params.verticalType],
    queryFn: async () => await assistantsService.fetchTemplates(params, isAccountPage),
    enabled: !!params.channel,
    staleTime: Infinity
  });

  return { data, isError, isLoading, refetch };
};

export const useFetchAvailableData = (params, isAccountPage) => {
  const hasPermission = useAppStore(hasPermissionSelector);
  const viewAllCollectionEntries = hasPermission(USER_PERMISSIONS.ALL_COLLECTION_ENTRIES.READ);
  const getCollectionEntries = (collection) => viewAllCollectionEntries ? collection : filterCollectionsByEnv(collection);

  const { data, isError, isLoading } = useQuery({
    queryKey: [FETCH_QUERY_KEYS.ASSISTANT_AVAILABLE_DATA, params.channel, params.languages, params.engagementType, params.verticalType],
    queryFn: async () => await assistantsService.fetchAvailableData(params, isAccountPage),
    enabled: !!params.channel && !!params.languages,
    select: (data) => ({
      [SKILLS]: getCollectionEntries(data.skills),
      [INTEGRATIONS]: getCollectionEntries(data.integrations),
      [BUILDING_BLOCKS]: getCollectionEntries(data.building_blocks)
    }),
    staleTime: Infinity
  });

  return { data, isError, isLoading };
};

export const useFetchLanguages = (allowRequest = true) => {
  const selectedAssistantId = useAppStore(assistantIdSelector);
  const selectedAccountId = useAppStore(selectedAccountIdSelector);
  const systemMode = useAppStore(systemModeSelector);

  const { isLoading, data, isError } = useQuery({
    queryKey: [FETCH_QUERY_KEYS.ASSISTANT_LANGUAGES, selectedAssistantId],
    queryFn: async () => await assistantsService.fetchLanguages({ selectedAccountId, selectedAssistantId }),
    enabled: !!selectedAssistantId && allowRequest,
    select: languages => {
      const live = getLanguages(languages.active);
      const draft = getLanguages(languages.draft);
      const current = systemMode === SYSTEM_MODES.EDIT && draft.list?.length > 0 ? draft : live;

      return { live, draft, current };
    },
    staleTime: Infinity
  });

  return { isLoading, data, isError };
};

export const useFetchSupportedLanguages = (isAccountPage) => {
  const { data, isError, isLoading } = useQuery({
    queryKey: [FETCH_QUERY_KEYS.SUPPORTED_LANGUAGES],
    queryFn: async () => await assistantsService.fetchSupportedLanguages(isAccountPage),
    staleTime: Infinity
  });

  return { data, isError, isLoading };
};

export const useFetchDefaultSettings = (params, isAccountPage) => {
  const { data, isError, isLoading } = useQuery({
    queryKey: [FETCH_QUERY_KEYS.ASSISTANT_DEFAULT_SETTINGS, params.assistantChannel],
    queryFn: async () => await assistantsService.fetchDefaultSettings(params, isAccountPage),
    enabled: !!params.assistantChannel,
    staleTime: Infinity
  });

  return { data, isError, isLoading };
};

export const useFetchVerticalTypes = (isAccountPage) => {
  const { data, isError, isLoading } = useQuery({
    queryKey: [FETCH_QUERY_KEYS.VERTICAL_TYPES],
    queryFn: async () => await assistantsService.fetchVerticalTypes(isAccountPage),
    staleTime: Infinity
  });

  return { data, isError, isLoading };
};

export const useFetchAllAssistantsData = () => useQuery({
  queryKey: [FETCH_QUERY_KEYS.ALL_ASSISTANTS_DATA],
  queryFn: async () => await assistantsService.fetchAllAssistantsData()
});

export const useFetchAgentKnowledge = (assistantId, mode) => {
  const systemMode = useAppStore(systemModeSelector);
  const previousSystemMode = useAppStore(previousSystemModeSelector);
  const isInEditMode = systemMode === SYSTEM_MODES.EDIT || (previousSystemMode === SYSTEM_MODES.EDIT && systemMode === SYSTEM_MODES.PREVIEW);
  const requestedMode = mode || (isInEditMode ? CONFIGURATION_VERSIONS.DRAFT : CONFIGURATION_VERSIONS.ACTIVE);

  return useQuery({
    queryKey: [FETCH_QUERY_KEYS.ASSISTANT_KNOWLEDGE, assistantId],
    queryFn: async () => await assistantsService.fetchAgentKnowledge(assistantId),
    enabled: !!assistantId,
    select: (data) => {
      if (requestedMode === CONFIGURATION_VERSIONS.DRAFT && data.draft) {
        return data.draft;
      }
      return data.active || null;
    }
  });
};

export const useFetchAssistantFile = () => useMutation({
  mutationFn: async ({ assistantId, assistantName }) => {
    const data = await assistantsService.getAssistantFile(assistantId);
    const fileName = `Agent Configuration - ${assistantName}.json`;
    downloadJsonFile(data, fileName);
    return data;
  }
});

export const useUploadAssistantConfig = () => useMutation({
  mutationFn: async ({ file, name, accountId }) => await assistantsService.uploadAssistantConfig(file, name, accountId)
});

export const useUpdateAgentKnowledge = () => useMutation({
  mutationFn: ({ assistantId, mode, knowledgeData }) => assistantsService.updateAgentKnowledge(assistantId, mode, knowledgeData)
});

export const useFetchAgentConfiguration = (assistantId) => {
  const systemMode = useAppStore(systemModeSelector);
  const isInEditMode = systemMode === SYSTEM_MODES.EDIT;

  return useQuery({
    queryKey: [FETCH_QUERY_KEYS.ASSISTANT_CONFIGURATION, assistantId],
    queryFn: async () => await assistantsService.fetchAgentConfiguration(assistantId),
    enabled: !!assistantId,
    select: (data) => {
      if (isInEditMode && data.draft) {
        return data.draft;
      }
      return data.active || null;
    }
  });
};

export const useSetMoveAgentToProduction = () => useMutation({
  mutationFn: async (data) => await assistantsService.setAssistantLive(data)
});

export const useDisableAgent = () => useMutation({
  mutationFn: async (data) => await assistantsService.disableAgent(data)
});

export const useResetAssistant = () => useMutation({
  mutationFn: async (data) => await assistantsService.resetAssistant(data)
});

export const useArchiveAssistant = () => useMutation({
  mutationFn: async (data) => await assistantsService.archiveAssistant(data)
});

export const useDuplicateAssistant = () => useMutation({
  mutationFn: async (data) => await assistantsService.duplicateAssistant(data)
});

export const useFetchCallMeSites = (assistantId, mode, isOutboundAgent) => {
  const NO_OPTIONS = [{ key: "", display_key: "No options available.", disabled: true }];

  return useQuery({
    queryKey: [FETCH_QUERY_KEYS.CALL_ME_SITES, assistantId, mode],
    queryFn: async () => await assistantsService.fetchCallMeSites(assistantId, mode),
    enabled: !!assistantId && !isOutboundAgent,
    select: (data) => {
      if (!data?.length) return NO_OPTIONS;
      return [{ key: "", display_key: "No selection" }, ...data];
    }
  });
};

export const useFetchCallMeBusinessHours = (assistantId, mode, isOutboundAgent) => {
  const NO_OPTIONS = [{ key: "", display_key: "No options available.", disabled: true }];

  return useQuery({
    queryKey: [FETCH_QUERY_KEYS.CALL_ME_BUSINESS_HOURS, assistantId, mode],
    queryFn: async () => await assistantsService.fetchCallMeBusinessHours(assistantId, mode),
    enabled: !!assistantId && !isOutboundAgent,
    select: (data) => {
      if (!data?.length) return NO_OPTIONS;
      return [{ key: "", display_key: "No selection" }, ...data];
    }
  });
};

export const useInitiateCall = () => useMutation({
  mutationFn: async (data) => await assistantsService.initiateCall(data)
});

export const useFetchAssistantsFeatures = () => useQuery({
  queryKey: [FETCH_QUERY_KEYS.ASSISTANTS_FEATURES],
  queryFn: async () => await assistantsService.fetchAssistantsFeatures()
});

export const useFetchPolicyCrossReferenceData = (enabled = true) => {
  const assistantId = useAppStore(assistantIdSelector);
  const policiesData = useAppStore(policiesDataSelector);

  const policyIds = policiesData?.policies?.local?.map(policy => policy.metadata.policyRef).filter(Boolean) || [];
  const sortedPolicyIdsKey = [...policyIds].toSorted().join(",");

  return useQuery({
    queryKey: [FETCH_QUERY_KEYS.POLICIES_LINKED_DATA, assistantId, sortedPolicyIdsKey],
    queryFn: async () => await getPolicyCrossReferenceData(assistantId, policyIds),
    enabled: enabled && !!assistantId && policyIds.length > 0
  });
};

export default {
  useFetchAssistant,
  useFetchTemplates,
  useFetchAvailableData,
  useFetchLanguages,
  useFetchSupportedLanguages,
  useFetchDefaultSettings,
  useFetchAllAssistantsData,
  useSetAssistantLive: useSetMoveAgentToProduction,
  useDeactivateAssistant: useDisableAgent,
  useResetAssistant,
  useArchiveAssistant,
  useDuplicateAssistant,
  useInitiateCall,
  useFetchPolicyCrossReferenceData,
  useFetchAssistantFile,
  useUploadAssistantConfig
};

